/**
 * @fileoverview Job Messaging Hook
 * 
 * Comprehensive hook for managing job-level messaging between clients and vendors.
 * Provides real-time messaging capabilities with contact information detection,
 * read status tracking, and message editing functionality.
 * 
 * Key Features:
 * - Real-time message subscription and updates
 * - Contact information detection with warning system
 * - Message read status tracking
 * - Unread message count calculation
 * - Message editing with validation
 * - Admin notification for flagged messages
 * 
 * @module useJobMessages
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useAuth } from '@/lib/auth';
import { validateMessageContent, ContactInfoDetection } from '@/utils/contactInfoDetection';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Represents a single message in a job conversation
 */
export interface JobMessage {
  id: string;
  job_id: string;
  sender_user_id: string;
  message_content: string;
  message_sequence: number;
  is_edited: boolean;
  edited_at: string | null;
  created_at: string;
  updated_at: string;
  organization_id: string;
  profiles?: {
    user_id: string;
    role: string;
  };
}

/**
 * Tracks which messages a user has read in a job
 */
export interface MessageReadStatus {
  id: string;
  job_id: string;
  user_id: string;
  last_read_at: string;
  last_message_sequence_read: number;
}

/**
 * Hook for managing job messages with real-time updates
 */
export const useJobMessages = (jobId: string) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<JobMessage[]>([]);
  const [readStatus, setReadStatus] = useState<MessageReadStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchMessages = useCallback(async () => {
    if (!jobId || !user) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch messages first
      const { data: messagesData, error: messagesError } = await supabase
        .from('job_messages')
        .select('*')
        .eq('job_id', jobId)
        .order('message_sequence', { ascending: true });

      if (messagesError) throw messagesError;

      // Fetch profiles for each unique sender
      const senderIds = [...new Set(messagesData?.map(m => m.sender_user_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, role')
        .in('user_id', senderIds);

      // Combine messages with profiles
      const messagesWithProfiles = messagesData?.map(message => ({
        ...message,
        profiles: profilesData?.find(p => p.user_id === message.sender_user_id)
      })) || [];

      setMessages(messagesWithProfiles as JobMessage[]);

      // Fetch read status for current user
      const { data: readStatusData, error: readStatusError } = await supabase
        .from('message_read_status')
        .select('*')
        .eq('job_id', jobId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (readStatusError) {
        throw readStatusError;
      }

      setReadStatus(readStatusData);

      // Calculate unread count
      const lastReadSequence = readStatusData?.last_message_sequence_read || 0;
      const totalMessages = messagesData?.length || 0;
      const unread = Math.max(0, totalMessages - lastReadSequence);
      setUnreadCount(unread);

    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  }, [jobId, user]);

  /**
   * Validates a message for contact information
   * Returns validation result without blocking
   */
  const validateMessage = useCallback((content: string): ContactInfoDetection => {
    return validateMessageContent(content);
  }, []);

  /**
   * Logs a flagged message and notifies admins
   */
  const logFlaggedMessage = useCallback(async (
    content: string,
    organizationId: string,
    detectionType: string,
    flaggedContent: string
  ) => {
    if (!user || !jobId) return;

    try {
      const { data: flaggedData, error: flagError } = await supabase
        .from('flagged_messages')
        .insert({
          user_id: user.id,
          organization_id: organizationId,
          job_id: jobId,
          message_content: content,
          detection_type: detectionType,
          flagged_content: flaggedContent,
          user_role: user.role,
          user_agent: navigator.userAgent,
          message_sent: true // Message was sent after warning
        })
        .select()
        .single();

      if (!flagError && flaggedData) {
        // Notify admins about the flagged message
        await invokeEdgeFunction('contact-info-notify', {
          body: {
            flaggedMessageId: flaggedData.id,
            jobId,
            userId: user.id,
            detectionType,
            flaggedContent
          },
          callerOperation: 'useJobMessages.logFlaggedMessage',
        });
      }
    } catch (logError) {
      console.error('Failed to log flagged message:', logError);
    }
  }, [user, jobId]);

  /**
   * Sends a message - validates but allows through with warning
   * Returns validation info so caller can show warning dialog
   */
  const sendMessage = useCallback(async (content: string, organizationId: string) => {
    if (!user || !jobId || !content.trim()) return;

    try {
      const { data, error } = await supabase
        .from('job_messages')
        .insert([{
          job_id: jobId,
          sender_user_id: user.id,
          message_content: content.trim(),
          organization_id: organizationId,
          message_sequence: 0 // Will be set by trigger
        }])
        .select()
        .single();

      if (error) throw error;

      // Get job and sender details for notification
      const { data: jobData } = await supabase
        .from('jobs')
        .select('public_id, title, organization_id')
        .eq('id', jobId)
        .single();

      if (jobData) {
        // Delegate notification + email to secure edge function (bypasses RLS safely)
        try {
          const { error } = await invokeEdgeFunction('message-notify', {
            body: { jobId, senderUserId: user.id },
            callerOperation: 'useJobMessages.sendMessage',
          });
          if (error) {
            console.error('message-notify invoke error', error);
          }
        } catch (e) {
          console.error('Failed to invoke message-notify', e);
        }
      }

      // Track message sent event
      analytics.track({
        name: ANALYTICS_EVENTS.MESSAGE_SENT,
        category: 'engagement',
        properties: {
          job_id: jobId,
          is_flagged: false,
        },
      });

      // Refresh messages after sending
      await fetchMessages();
      return { success: true, data };
    } catch (err) {
      console.error('Error sending message:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to send message'
      };
    }
  }, [user, jobId, fetchMessages]);

  /**
   * Sends a message that was flagged for contact info
   * Logs the flagged content and then sends the message
   */
  const sendFlaggedMessage = useCallback(async (
    content: string,
    organizationId: string,
    detectionType: string,
    flaggedContent: string
  ) => {
    if (!user || !jobId || !content.trim()) return;

    // First log the flagged message
    await logFlaggedMessage(content, organizationId, detectionType, flaggedContent);

    // Then send the actual message
    return sendMessage(content, organizationId);
  }, [user, jobId, logFlaggedMessage, sendMessage]);

  const editMessage = useCallback(async (messageId: string, newContent: string, organizationId: string) => {
    if (!user || !newContent.trim()) return;

    try {
      const { data, error } = await supabase
        .from('job_messages')
        .update({
          message_content: newContent.trim(),
          is_edited: true,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      // Refresh messages after editing
      await fetchMessages();
      return { success: true, data };
    } catch (err) {
      console.error('Error editing message:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to edit message'
      };
    }
  }, [user, fetchMessages]);

  /**
   * Edits a message that was flagged for contact info
   * Logs the flagged content and then edits the message
   */
  const editFlaggedMessage = useCallback(async (
    messageId: string,
    newContent: string,
    organizationId: string,
    detectionType: string,
    flaggedContent: string
  ) => {
    if (!user || !newContent.trim()) return;

    // First log the flagged message
    await logFlaggedMessage(newContent, organizationId, detectionType, flaggedContent);

    // Then edit the actual message
    return editMessage(messageId, newContent, organizationId);
  }, [user, logFlaggedMessage, editMessage]);

  const markAsRead = useCallback(async () => {
    if (!user || !jobId || messages.length === 0) return;

    try {
      const latestSequence = Math.max(...messages.map(m => m.message_sequence));
      
      const { error } = await supabase
        .from('message_read_status')
        .upsert({
          job_id: jobId,
          user_id: user.id,
          last_read_at: new Date().toISOString(),
          last_message_sequence_read: latestSequence
        }, {
          onConflict: 'job_id,user_id'
        });

      if (error) throw error;

      setUnreadCount(0);
      setReadStatus(prev => prev ? {
        ...prev,
        last_read_at: new Date().toISOString(),
        last_message_sequence_read: latestSequence
      } : {
        id: '',
        job_id: jobId,
        user_id: user.id,
        last_read_at: new Date().toISOString(),
        last_message_sequence_read: latestSequence
      });

    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [user, jobId, messages]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Real-time subscription for new messages - stable dependencies
  useEffect(() => {
    if (!jobId || !user) return;

    const channel = supabase
      .channel(`job-messages-${jobId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_messages',
          filter: `job_id=eq.${jobId}`
        },
        () => {
          // Call fetchMessages directly without dependency
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId, user?.id]); // Only depend on stable values

  return {
    messages,
    readStatus,
    unreadCount,
    loading,
    error,
    validateMessage,
    sendMessage,
    sendFlaggedMessage,
    editMessage,
    editFlaggedMessage,
    markAsRead,
    refetch: fetchMessages
  };
};
